import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { onboardingService } from "../../services/onboarding/onboardingService";
import { useAuth } from "../../contexts/AuthContext";

const steps = ["Welcome", "Direct Connect", "Add Account"];

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
  onSkip?: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  open,
  onComplete,
  onSkip,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      // Last step - navigate to accounts page to trigger Plaid Link
      await onboardingService.markOnboardingComplete();
      onComplete();
      navigate("/accounts");
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = async () => {
    if (onSkip) {
      await onboardingService.skipOnboarding();
      onSkip();
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        // Welcome step
        return (
          <Box sx={{ minHeight: 300 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Welcome to Focus: Finance, {user?.firstName || "there"}! 👋
            </Typography>

            <Typography variant="body1" paragraph color="text.secondary">
              Let's get your account set up in just a few steps. This will only
              take a couple of minutes.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Here's what Focus: Finance can do for you:
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <AccountBalanceIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Connect Bank Accounts"
                  secondary="Securely link your bank accounts and credit cards"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <TrendingUpIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Track Transactions"
                  secondary="Automatically import and categorize your transactions"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <ReceiptIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Manage Expenses"
                  secondary="Track expenses, income, and debts in one place"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <AttachMoneyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Financial Insights"
                  secondary="Get insights into your spending patterns and financial health"
                />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Next:</strong> Connect your first bank account securely
                with Plaid.
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        // Direct Connect (Prime Agent) — optional
        return (
          <Box sx={{ minHeight: 300 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Banks Plaid can't reach? Meet Prime Agent.
            </Typography>

            <Typography variant="body1" paragraph color="text.secondary">
              Some banks use "Direct Connect" and can't be linked through Plaid.
              Prime Agent can log into those for you and import your transactions
              automatically — optional, and you can set it up anytime.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "primary.light",
                color: "primary.contrastText",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AccountBalanceIcon sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Prime Agent Direct Connect</Typography>
                  <Typography variant="body2">
                    Auto-import from banks aggregators can't connect to
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={() => window.open("/prime-agent", "_blank")}
              >
                Learn about Prime Agent
              </Button>
              <Button variant="outlined" onClick={() => navigate("/settings")}>
                Connect in Settings
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Prefer Plaid? Just click <strong>Next</strong> — you can add
                Prime Agent later from Settings.
              </Typography>
            </Alert>
          </Box>
        );

      case 2:
        // Add account step
        return (
          <Box sx={{ minHeight: 300 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Add Your First Account
            </Typography>

            <Typography variant="body1" paragraph color="text.secondary">
              You're all set! Now let's connect your first bank account.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "primary.light",
                color: "primary.contrastText",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CheckCircleIcon sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Ready to Connect!</Typography>
                  <Typography variant="body2">
                    Click "Connect Account" to securely link your bank
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              What happens next:
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Secure Connection"
                  secondary="You'll be redirected to your bank's secure login"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <AccountBalanceIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Select Accounts"
                  secondary="Choose which accounts to connect"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <TrendingUpIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Import Transactions"
                  secondary="Your recent transactions will be imported automatically"
                />
              </ListItem>
            </List>

            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Click <strong>Add First Account</strong> to go to the Accounts
                page and connect your bank!
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 600 },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Getting Started</Typography>
          {activeStep > 0 && (
            <Button onClick={handleSkip} color="inherit" size="small">
              Skip Setup
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>

        <Box sx={{ flex: "1 1 auto" }} />

        {activeStep === 0 && onSkip && (
          <Button onClick={handleSkip} color="inherit">
            Skip for Now
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={
            activeStep === steps.length - 1 ? (
              <AccountBalanceIcon />
            ) : (
              <ArrowForwardIcon />
            )
          }
        >
          {activeStep === steps.length - 1 ? "Connect Bank Account" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingWizard;
